import React, { useCallback, useMemo } from 'react';
import type { BestAvailableOrderProps, EventOrderStatProps, MarketProps, MarketSideOptionProps, OrderProps, TeamProps, TournamentProps, TradeProps } from "../../../types"
import { view_styles } from '../../../constants/styles';
import MarketButton from '../MarketButton';
import { Icons, LinearDiagnal } from '../../../Components';
import { MarketButtonHelpers } from '../MarketButton/api';
import { MarketComponentHelpers } from '../../api';
import { TeamImage } from '../../../Components/Jerseys';
import { Button, Text, View } from '../../../Components/Themed';
import { useColors } from '../../../constants/useColors';

type TeamTournamentMarketProps = {
    tournament:TournamentProps,
    event_type:'team'|'tournament'|'match',
    team:TeamProps,
    image_size?:number,
    hide_stat_banner?:boolean,
    width?:number,
    float?:boolean,
    disabled_markets?:string[],
    market:MarketProps,
    direction?:'horizontal'|'vertical',
    show_event?:boolean,
    hide_liquidity?:boolean,
    style?:any,
    highlight_router_orders?:boolean,
    latest_trades:TradeProps[],
    best_available_orders:BestAvailableOrderProps[],
    event_order_stats:EventOrderStatProps[],
    default_price_view:'last_trade'|'best_available',
    onOrder:(order:OrderProps) => void,
    show_grades?:boolean,
    onView:(data:{event_id:string, event_type:string, market_id:string, side_type:'team', side_id:string}) => void,
    onTradeLongPress:(trade:TradeProps) => void
}

const TeamTournamentMarket = ({ tournament, image_size, highlight_router_orders, disabled_markets, float, direction, show_event, hide_liquidity, hide_stat_banner, style, team, market, event_order_stats, default_price_view, latest_trades, show_grades, best_available_orders, onOrder, onView, onTradeLongPress }:TeamTournamentMarketProps) => {
    const C = useColors();
    const { liquidity, open_order_count } = MarketComponentHelpers.getLiqudity(event_order_stats);
    const is_locked = useMemo(() => MarketComponentHelpers.isTournamentMarketLocked(tournament, market.market_id),[JSON.stringify(tournament.supported_markets)])
    const renderSides = useCallback((data:{ item:MarketSideOptionProps, index:number, market:MarketProps }) => {
        
        const ba_order = best_available_orders.find(o => o.market_id == market.market_id && o.side_type == 'team' && o.side_id == team.team_id && o.side == data.item.side && o.show);
        const side_details = MarketComponentHelpers.getTournamentSideIds(market, data.item, undefined, team);
        if(!side_details){ return <></> }
        let trade = MarketButtonHelpers.getVisibleTrade(default_price_view, data.market, data.item, latest_trades, best_available_orders, side_details.side_id, side_details.reversed_side_id); 
        if(!trade){
            trade = MarketButtonHelpers.getTradeFromMarket(market, tournament.tournament_id, 'team', data.item.side, data.item.id_source, side_details.side_id, side_details.reversed_side_id);
        }
        let disabled = false
        if(disabled_markets && disabled_markets.includes(data.market.market_id.toString())){ disabled = true }
        return (
            <View key={`${tournament.tournament_id}_${team.team_id}_${data.item.side}`} style={{ margin:2, height:50, width:75 }}>
                <MarketButton
                    side_option={data.item}
                    market={data.market}
                    trade={trade}
                    locked={is_locked || disabled}
                    show_grades={show_grades}
                    highlight_router_orders={highlight_router_orders}
                    order={ba_order}
                    onLongPress={onTradeLongPress}
                    onPress={(order) => {
                        const order_title = MarketComponentHelpers.getOrderTitleForTournament(order, market, tournament, undefined, team);
                        if(!order_title){ return alert('Unable to process order') }
                        onOrder({ ...order, title: order_title, league_id: tournament.league_id })
                    }}
                />
            </View>
        )
    },[JSON.stringify(best_available_orders), JSON.stringify(disabled_markets), default_price_view])

    return (
        <View float={float} style={[{ borderRadius:8 }, style]}>
            {!hide_stat_banner ?
             <LinearDiagnal 
                label={market.stat_label ?? market.type}
                label_size={14}
                style={{ borderTopLeftRadius:8, padding:5  }}
                left_color={C.text.gold}
                right_color={'#ebd197'}
            />
            :<></>}
            {show_event ?
            <View transparent style={{ padding:5 }}>
                <Text theme='description'>{tournament.tournament_name}</Text>
            </View>
            :<></>}
            <View transparent style={{ flexDirection:direction == 'horizontal' ? 'row' : 'column', flexGrow:1 }}>
                <View transparent style={{ ...view_styles.section_body, flexDirection:direction == 'horizontal' ? 'row' : 'column', flexGrow:2, padding:0, justifyContent:'center', alignItems:'center' }}>
                    <TeamImage
                        team={team}
                        size={image_size ?? 24}
                    />
                    <View transparent style={{ padding:10, paddingBottom:0 }}>
                        <Text theme='h2' textAlign='center'>{team.market_name} {team.name}</Text>
                        <Text style={{ marginTop:3 }} theme='light' textAlign='center' size={12}>{team.division}</Text>
                    </View>
                </View>
                <View transparent nativeID='market_sides' style={{ flexDirection:'row',flexGrow:1.5, justifyContent:'center', alignItems:'center', padding:10 }}>
                    {market.side_options.sort((a,b) => {
                        let a_t = a.side == market.trade_side ? 1 : 0
                        let b_t = b.side == market.trade_side ? 1 : 0
                        return a_t - b_t
                    }).map((so, index) => {
                        return renderSides({ item:so, index, market })
                    })}
                </View>
            </View>
            {!hide_liquidity ?
            <View type='footer' style={{ flexDirection:'row', alignItems:'center', borderBottomLeftRadius:8, borderBottomRightRadius:8 }}>
                <Button transparent style={{ flexGrow:1, flexDirection:'row', padding:10 }} onPress={() => {
                    onView({
                        event_id: tournament.tournament_id, 
                        event_type: 'tournament', 
                        market_id: market.market_id, 
                        side_type: 'team', 
                        side_id: team.team_id
                    });
                }}>
                    <View float style={{ height:30, width:30, borderRadius:100, justifyContent:'center', alignItems:'center' }}>
                        <Icons.LiquidityIcon color={C.text.h1} size={30}/>
                    </View>
                    <View transparent style={{ marginLeft:10 }}>
                        <Text theme='description'>Liquidity</Text>
                        <Text size={12} style={{ marginTop:3 }} color={C.text.highlight} weight='semibold'>${liquidity.toFixed()} {open_order_count} Orders</Text>
                    </View>
                </Button>
            </View>
            :<></>}
        </View>
    )
}


export default React.memo(TeamTournamentMarket, (left, right) => {
    if(JSON.stringify(left) == JSON.stringify(right)){ return true }
    return false
})